import QRCode from 'react-qr-code';
import styled from 'styled-components';
import React, { useEffect, useState } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { removeModal } from '../../../redux/actions/modal';
import { addNotification } from '../../../redux/actions/notification';

// Types & Enums
import { ButtonThemes } from '../../../enums/button-themes';
import { FieldValues } from 'react-hook-form';
import { NotificationMessageType } from '../../../enums/notification-types';

// Hooks
import usePostResourceWithPayload from '../../../react-query/hooks/use-post-resource-with-payload';

// Config
import {
    CONFIG_REMOVE_MFA,
    CONFIG_SET_MFA_GET_QR,
    CONFIG_SET_MFA_TOKEN_CONFIRMATION,
} from '../../../configurations/resources-config';

// Components
import LoadingSpinner from '../../../components/loading-spinner';
import InputButton from '../../../components/inputs/input-button';

import { RootState } from '../../../redux/store';
import Form from '../../../components/forms/form';
import { setUser } from '../../../redux/actions/user';
import { TooltipPopUpSide } from '../../../components/tooltip';
import ModalNavigation from '../../../components/modal-navigation';
import validators from '../../../components/forms/validation/validators';
import useFetchResource from '../../../react-query/hooks/use-fetch-resource';

const StyledFormContainer = styled.div`
    background-color: ${props => props.theme.colours.white};
    padding: 20px;
`;
type QRCodeData = {
    otpAuthUrl: string;
};

const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    margin-top: 20px;
`;

const LayoutModalSetMFACodeDevice = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user);

    const [qrCode, setQrCode] = useState<string | null>(null);
    const [tokenSetupConfirmStage, setTokenSetupConfirmStage] = useState<boolean>(false);

    const qrCodeQuery = useFetchResource({
        resource: CONFIG_SET_MFA_GET_QR,
        params: [],
        staleTime: 0,
        select: (data: QRCodeData) => {
            return data.otpAuthUrl;
        },
    });
    useEffect(() => {
        // Fetch QR code when component mounts
        if (qrCodeQuery.data) {
            setQrCode(qrCodeQuery.data);
        }
    }, [qrCodeQuery.data]);

    const confirmSetupMFAMutation = usePostResourceWithPayload({
        resource: CONFIG_SET_MFA_TOKEN_CONFIRMATION,
        handleOnSuccess: () => {
            dispatch(
                addNotification({
                    copy: 'Multi-Factor Authentication setup was successful.',
                    type: NotificationMessageType.Success,
                })
            );
            dispatch(removeModal());
            dispatch(setUser({ ...user, mfaEnabled: true }));
            setTokenSetupConfirmStage(false);
        },
        handleOnError: error => {
            dispatch(
                addNotification({
                    copy:
                        error ||
                        'There was an issue trying to set up your Multi-Factor Authentication device. Please try again later.',
                    type: NotificationMessageType.Error,
                })
            );
        },
    });
    const confirmRemoveMFAMutation = usePostResourceWithPayload({
        resource: CONFIG_REMOVE_MFA,
        handleOnSuccess: () => {
            dispatch(
                addNotification({
                    copy: 'Multi-Factor Authentication removal was successful.',
                    type: NotificationMessageType.Success,
                })
            );
            dispatch(removeModal());
            dispatch(setUser({ ...user, mfaEnabled: false }));
        },
        handleOnError: error => {
            dispatch(
                addNotification({
                    copy:
                        error ||
                        'There was an issue trying to set up your Multi-Factor Authentication device. Please try again later.',
                    type: NotificationMessageType.Error,
                })
            );
        },
    });

    const handleSetupMFA = () => {
        setTokenSetupConfirmStage(true);
    };

    const handleTokenSetupConfirmation = (data: FieldValues) => {
        confirmSetupMFAMutation.mutate({
            token: data.mfaToken,
        });
    };
    const handleTokenRemoveConfirmation = (data: FieldValues) => {
        confirmRemoveMFAMutation.mutate({
            token: data.mfaToken,
        });
    };

    const RenderConfirmSetupMFAToken = () => {
        return (
            <StyledFormContainer>
                <Form onSubmit={handleTokenSetupConfirmation}>
                    <Form.Body>
                        <Form.Section>
                            <Form.Field>
                                <Form.InputLabel
                                    htmlFor="mfaToken"
                                    label="Multi-Factor Authentication Code to Confirm Setup"
                                    required={true}
                                    tooltipCopy="Please enter the Multi-Factor Authentication Code from your authenticator app to finalize the Multi Factor Authentication after you scanned the QR code in the previous page."
                                    tooltipPosition={TooltipPopUpSide.Right}
                                />
                                <Form.InputText
                                    name="mfaToken"
                                    placeholder="Multi-Factor Authentication Code..."
                                    validators={[validators.required, validators.isNumber, validators.length(6)]}
                                />
                            </Form.Field>
                        </Form.Section>
                        <Form.Section>
                            <Form.Field>
                                <></>{' '}
                            </Form.Field>
                        </Form.Section>
                    </Form.Body>
                    <Form.Footer>
                        <Form.InputButton
                            value="Confirm Setup Multi-Factor Authentication"
                            loading={confirmSetupMFAMutation.isPending}
                            disabled={confirmSetupMFAMutation.isPending}
                            buttonTheme={ButtonThemes.Primary}
                        />
                    </Form.Footer>
                </Form>
            </StyledFormContainer>
        );
    };
    const RenderConfirmRemoveMFAToken = () => {
        return (
            <StyledFormContainer>
                <p>
                    Your account is already set up with multi-factor authentication; however, you can remove it by
                    confirming the code in the textbox below from the authenticator app.
                </p>
                <Form onSubmit={handleTokenRemoveConfirmation}>
                    <Form.Body>
                        <Form.Section>
                            <Form.Field>
                                <Form.InputLabel
                                    htmlFor="mfaToken"
                                    label="Multi-Factor Authentication Code"
                                    required={true}
                                    tooltipCopy="Please enter the Multi-Factor Authentication Code from your authenticator app to remove the Multi Factor Authentication."
                                    tooltipPosition={TooltipPopUpSide.Right}
                                />
                                <Form.InputText
                                    name="mfaToken"
                                    placeholder="Multi-Factor Authentication Code..."
                                    validators={[validators.required, validators.isNumber, validators.length(6)]}
                                />
                            </Form.Field>
                        </Form.Section>
                        <Form.Section>
                            <Form.Field>
                                <></>{' '}
                            </Form.Field>
                        </Form.Section>
                    </Form.Body>
                    <Form.Footer>
                        <Form.InputButton
                            value="Remove Multi-Factor Authentication"
                            loading={confirmRemoveMFAMutation.isPending}
                            disabled={confirmRemoveMFAMutation.isPending}
                            buttonTheme={ButtonThemes.Primary}
                        />
                    </Form.Footer>
                </Form>
            </StyledFormContainer>
        );
    };

    const RenderSetupMFA = () => {
        if (!qrCode || qrCodeQuery.isPending) {
            return (
                <StyledFormContainer>
                    <LoadingSpinner />
                </StyledFormContainer>
            );
        }
        return (
            <StyledFormContainer>
                <p>
                    Please scan the QR Code below, in an authenticator app, and press the SET UP Multi-Factor
                    Authentication button, where you will be asked to confirm the Multi-Factor Authentication token.
                    Then, your account will be set up with multi-factor authentication.
                </p>
                <QRCode value={qrCode} />
                <StyledButtonContainer>
                    <InputButton value={`Set UP Multi-Factor Authentication`} onClick={handleSetupMFA} />
                </StyledButtonContainer>
            </StyledFormContainer>
        );
    };

    return (
        <>
            <ModalNavigation
                buttons={[
                    {
                        value: 'CLOSE',
                        onClick: () => dispatch(removeModal()),
                        buttonTheme: ButtonThemes.Secondary,
                    },
                ]}
            />
            {!user.mfaEnabled ? (
                <h2>Set up Multi-Factor Authentication</h2>
            ) : (
                <h2>Remove Multi-Factor Authentication Code Device</h2>
            )}

            <StyledFormContainer>
                {!user.mfaEnabled && (tokenSetupConfirmStage ? <RenderConfirmSetupMFAToken /> : <RenderSetupMFA />)}
                {user.mfaEnabled && <RenderConfirmRemoveMFAToken />}
            </StyledFormContainer>
        </>
    );
};

export default LayoutModalSetMFACodeDevice;
